/**
 * Prasaran is a wrapper around Pusher-js.
 * It provides a HOC (withPrasaran) which can be used to subscribe to a channel
 * and then it provides an event binding called bindSandesh, which can be used to
 * bind an event key with a callback.
 */
import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect
} from "react";
// @ts-expect-error TS5 upgrade
import type { EventCallback } from "pusher-js";
import Pusher from "pusher-js";
import { apiBaseURL, tenant, RequestHeaders } from "@certa/network";
import { subscriptions } from "../helpers";
import type {
  PrasaranHOCProps,
  PrasaranEventType,
  EventBindingMap
} from "../types";
import memoizeOne from "memoize-one";
import { v4 as uuid } from "uuid";
import { usePrevious } from "@certa/common/hooks";

const PUSHER_KEY = import.meta.env.REACT_APP_PUSHER_KEY;
const prasaranChannelPrefix = `${
  import.meta.env.REACT_APP_PRASARAN_PREFIX
}_${tenant}`;
const AUTH_URL = apiBaseURL + "prasaran/auth/";

let socket: Pusher;
let isConnected: boolean = false;

// enable logs when not in production mode
Pusher.logToConsole = !import.meta.env.PROD;

// Enabling credentials in Pusher Authentication XHR
// @ts-expect-error TS5 upgrade
Pusher.Runtime.createXHR = () => {
  const xhr = new XMLHttpRequest();
  xhr.withCredentials = true;
  return xhr;
};

function connectPrasaran() {
  if (PUSHER_KEY) {
    // Initializing Pusher instance
    socket = new Pusher(PUSHER_KEY, {
      cluster: "mt1",
      forceTLS: true,
      channelAuthorization: {
        endpoint: AUTH_URL,
        transport: "ajax",
        headers: RequestHeaders.PRASARAN
      }
    });
    isConnected = true;
    return true;
  } else {
    isConnected = false;
    return false;
  }
}

export function disconnectPrasaran() {
  if (socket) {
    socket.disconnect();
    isConnected = false;
  }
}

/**
 * Derives the complete channel name with prefixes and everything.
 * This is not directly used, but it's value is rather used to compare
 * and update a class property based on certain conditions.
 */
const generateChannelName = (channelName: string) =>
  channelName ? `private-${prasaranChannelPrefix}_${channelName}` : "";

/**
 * Subscribes to a particular channel name and provides methods to bind
 * callback to certain events.
 * This hook also automatically:
 * - Switches subscription if the channel name is changed.
 * - Rebinds events whenever the subscription is re-established
 * @param {string} _channelName
 */
export const usePrasaran = (
  _channelName?: string,
  shouldAddPrefixToChannelName: boolean = true
) => {
  const channelName = useMemo(
    () =>
      _channelName
        ? shouldAddPrefixToChannelName
          ? generateChannelName(_channelName)
          : _channelName
        : null,
    [_channelName, shouldAddPrefixToChannelName]
  );
  const componentKeyRef = useRef(uuid()); // auto-generated uuid based component key
  const eventBindings = useRef<EventBindingMap>({});
  const isMounted = useRef(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscribedChannelName, setSubscribedChannelName] =
    useState<string>("");

  /**
   * To keep a record of when the component is being mounted and unmounted
   * so that we can run necessary cleanups accordingly
   */
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const currentSubscription = useMemo(() => {
    return subscriptions[subscribedChannelName]
      ? subscriptions[subscribedChannelName].subscription
      : null;
  }, [subscribedChannelName]);

  /**
   * Adds binding of any event and it's callback to the channel
   * subscription, if it's subscribed to.
   */
  const bindEventToPusher = useCallback(
    (event: PrasaranEventType, callback: EventCallback) => {
      if (currentSubscription && isSubscribed) {
        // Only if we're a subscription ready, we should bind, otherwise
        // as soon as the bindings will be done automatically, later.
        currentSubscription.bind(event.name, callback);
      }
    },
    [currentSubscription, isSubscribed]
  );

  /**
   * Removes binding of any event and it's callback from the channel
   * subscription, if it's subscribed to.
   */
  const unbindEventFromPusher = useCallback(
    (event: PrasaranEventType, callback: EventCallback) => {
      if (currentSubscription) {
        currentSubscription.unbind(event.name, callback);
      }
    },
    [currentSubscription]
  );

  /**
   * To bind all the events that we've maintained stored the bindings for.
   * This is helpful when subscription is not ready to take evening bindings
   * at some point, but we can keep storing the bindings in the class property
   * and when the subscription is ready, we can use this method to bind all
   * of them in a single go.
   */
  const bindAllEvents = useCallback(() => {
    Object.keys(eventBindings.current).forEach(eventName => {
      Array.from(eventBindings.current?.[eventName] || []).forEach(action => {
        bindEventToPusher({ name: eventName }, action);
      });
    });
  }, [bindEventToPusher]);

  /**
   * To unbind all the events that we've maintained stored the bindings for.
   * Useful when component is being unmounted or channel subscription is being
   * changed.
   */
  const unbindAllEvents = useCallback(() => {
    Object.keys(eventBindings.current).forEach(eventName => {
      Array.from(eventBindings.current?.[eventName] || []).forEach(action => {
        unbindEventFromPusher({ name: eventName }, action);
      });
    });
  }, [unbindEventFromPusher]);

  /**
   * When the channel Subscription succeeds (after auth is done), then
   * we need to make sure that component's state is updated for the subscription
   * so that events dependent on that can take place (like event bindings)
   */
  const handleSubscriptionSucceeded = useCallback((subChannelName: string) => {
    // Guard check to prevent race condition where subscription is cleaned up
    // before the success event fires
    if (subscriptions[subChannelName]) {
      subscriptions[subChannelName].isReady = true;
      setIsSubscribed(true);
    }
  }, []);

  /**
   * Create a subscription to a channel or reuse one if it exists.
   * Also registers the component among the subscribers to this
   * particular channel.
   */
  const subscribeToChannel = useCallback(
    (subChannelName: string) => {
      if (!PUSHER_KEY) return; // No need to proceed if Pusher isn't active

      // Create a pusher connection if it's not already been created
      if (!isConnected) connectPrasaran();

      setIsSubscribed(false);

      // Check if we already have a subscription for the channel
      if (subscriptions[subChannelName]) {
        // If we do, then we shall use the same subscription here.
        subscriptions[subChannelName].subscribers.add(componentKeyRef.current);

        if (subscriptions[subChannelName].isReady) {
          // if the subscription that already existed is ready to take
          // event bindings then we can just go ahead and do it.
          handleSubscriptionSucceeded(subChannelName);
        } else {
          // otherwise, we'll also monitor the succeeded event directly
          // and make event bindings when that event occurs.
          subscriptions[subChannelName].subscription.bind(
            "pusher:subscription_succeeded",
            handleSubscriptionSucceeded.bind(null, subChannelName)
          );
        }
      } else {
        // But, if we don't, have existing subscription for the current channeName
        // then we create a new subscription
        const subscription = socket.subscribe(subChannelName);
        // And store it's reference in the map (to be reused later)
        subscriptions[subChannelName] = {
          subscription,
          // since this component is the first subscriber of the channel, it's the only
          // subscriber as well, at the moment.
          subscribers: new Set([componentKeyRef.current]),
          isReady: false
        };

        // To monitor the subscription's success and update in state accordingly
        subscription.bind(
          "pusher:subscription_succeeded",
          handleSubscriptionSucceeded.bind(null, subChannelName)
        );
      }
    },
    [handleSubscriptionSucceeded]
  );

  /**
   * Unsubscribe does not always mean that the actual Pusher's underlying
   * unsubscribe method will be called. Rather we just make sure to unbind
   * the events from the subscription that this component has made.
   *
   * ONLY when the subscribers count is nil for a particular channel, the
   * underlying pusher's channel unsubscribe method is invoked to stop
   * listening to that particular channel.
   */
  const unSubscribeFromChannel = useCallback((unsubChannelName: string) => {
    if (!isConnected) return;
    setIsSubscribed(false);

    // Delete self from the subscribers
    subscriptions[unsubChannelName]?.subscribers.delete(
      componentKeyRef.current
    );

    // if there are no subscribers left, unsubscribe the channel altogether
    if (subscriptions[unsubChannelName]?.subscribers.size === 0) {
      socket.unsubscribe(unsubChannelName);
      delete subscriptions[unsubChannelName];
    }
  }, []);

  /**
   * To monitor the change in channel name, and when that happens
   * we need to shift subscriptions to the new one accordingly
   */
  useEffect(() => {
    if (subscribedChannelName !== channelName) {
      if (subscribedChannelName && subscriptions[subscribedChannelName]) {
        unSubscribeFromChannel(subscribedChannelName);
      }
      if (channelName) {
        setSubscribedChannelName(channelName);
        subscribeToChannel(channelName);
      }
    }
  }, [
    setSubscribedChannelName,
    channelName,
    subscribedChannelName,
    unSubscribeFromChannel,
    subscribeToChannel
  ]);

  const isSubscribedPrev = usePrevious(isSubscribed); // to keep last subscribed state
  /**
   * To trigger the subscription change post-effects where we need
   * to rebind all the events
   */
  useEffect(() => {
    if (isSubscribed && !isSubscribedPrev) {
      bindAllEvents();
    } else if (!isSubscribed && isSubscribedPrev) {
      unbindAllEvents();
    }
  }, [isSubscribed, bindAllEvents, isSubscribedPrev, unbindAllEvents]);

  /**
   * Effect to unsubscribe from channel when the component unmounts
   */
  useEffect(() => {
    return () => {
      if (!isMounted.current && channelName && currentSubscription) {
        unSubscribeFromChannel(channelName);
      }
    };
  }, [channelName, currentSubscription, unSubscribeFromChannel]);

  /**
   * Removes event binding from the set of bindings for this particular
   * component.
   * Also, removes it from the channel subscription, if the subscription
   * is ready.
   */
  const unbindEvent = useCallback(
    (event: PrasaranEventType, callback: EventCallback) => {
      if (eventBindings.current?.[event.name]) {
        eventBindings.current[event.name].delete(callback);
      }

      unbindEventFromPusher(event, callback);
    },
    [unbindEventFromPusher]
  );

  /**
   * Adds event binding to the set of bindings for this particular
   * component.
   * Also, binds it to the channel subscription, if the suscription
   * is ready, otherwise the class maintained event map is used to
   * make bindings later
   */
  const bindEvent = useCallback(
    (event: PrasaranEventType, callback: EventCallback) => {
      eventBindings.current[event.name] =
        eventBindings.current[event.name] || new Set();
      eventBindings.current[event.name].add(callback);

      bindEventToPusher(event, callback);
    },
    [bindEventToPusher]
  );

  return {
    bindSandesh: bindEvent,
    unbindSandesh: unbindEvent,
    isSubscribed: isSubscribed
  };
};

/**
 * Prasaran HOC is responsible for subscribing to any channel
 * and provide a method to bind events to callbacks
 */
export const withPrasaran =
  (channelSelector: Function) =>
  (
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ChildComponent: React.ElementType
  ) => {
    // @ts-expect-error TS5 upgrade
    const memoizedChannelSelector = memoizeOne(channelSelector);

    return (props: PrasaranHOCProps) => {
      const { bindSandesh, unbindSandesh, isSubscribed } = usePrasaran(
        memoizedChannelSelector(props)
      );
      // Props to be passed to child component
      const prasaranProps = {
        onSandesh: bindSandesh,
        unbindSandesh: unbindSandesh,
        prasaranChannel: memoizedChannelSelector(props),
        isSubscribed: isSubscribed
      };

      return <ChildComponent {...props} {...prasaranProps} />;
    };
  };
